import { Redirect, Router } from '@reach/router';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';

import { useRoutingData } from '@xingternal/crate-communication-data-layer';
import { RootProvider } from '@xingternal/crate-communication-root-provider';
import { useFeatureSwitches } from '@xingternal/crate-hooks-use-feature-switches';
import { useLoginState } from '@xingternal/crate-hooks-use-login-state';
import { ScrollManager } from '@xingternal/crate-layout-scroll-memo-module';
import { NoSSR } from '@xingternal/crate-utils-no-ssr';
import type { Xinglet, RuntimeHost } from '@xingternal/crate-xinglet';
import { useRuntimeConfig } from '@xingternal/crate-xinglet';
import GlobalStyles from '@xingternal/global-styles';
import { XingHubProvider } from '@xingternal/xing-hub';
import { parseCookies } from '@xingternal/xing-hub/lib/parseCookies';

import { NotFound } from './error-components';
import {
  LoggedInFrameProvider,
  LoggedOutFrameProvider,
} from './frame-provider';
import { initTracking } from './init-tracking';
import { internalPaths } from './internal-paths';
import { Route } from './route';
import type { ActiveItem } from './types';
import { useRegisterExperiments } from './use-register-experiments';

type RoutingProps = {
  setActiveItem: React.Dispatch<ActiveItem>;
  setDisableCookieConsentBanner: React.Dispatch<boolean>;
  routesScope?: string;
};

const Routing: React.VFC<RoutingProps> = ({
  setActiveItem,
  setDisableCookieConsentBanner,
  routesScope,
}) => {
  let routingData = useRoutingData();

  if (routesScope === 'lebenslauf') {
    routingData = routingData
      .filter((data) => data.route.startsWith('lebenslauf'))
      .map((data) => ({
        ...data,
        route: data.route
          .replace('lebenslauf/', '/')
          .replace('lebenslauf', '/'),
      }));
  }

  return (
    <Router>
      {routingData.map((route) => (
        <Route
          path={route.route}
          data={route}
          // All children have the same key to avoid re-mounting when the
          // route changes:
          key="static"
          setActiveItem={setActiveItem}
          setDisableCookieConsentBanner={setDisableCookieConsentBanner}
        />
      ))}
      <NotFound default />
    </Router>
  );
};

// calls ttt.init and sets up the tracking environment
// should always be the first call in the root provider
const ttt = initTracking();

const cookies = parseCookies(
  globalThis.document ? globalThis.document.cookie : ''
);

const JobsX3Redirects: React.FC = () => {
  const featureSwitches = useFeatureSwitches();
  const isVisionTypeEnabled = featureSwitches?.includes('enable_vision_type');

  if (isVisionTypeEnabled) return null;

  return (
    <Router>
      <Redirect from="/jobs/find" to="/jobs/search?keywords=Find" />
      <Redirect from="/jobs/my-jobs" to="/jobs/jobbox" />
    </Router>
  );
};

const Root: React.FC<{
  host: RuntimeHost;
  routesScope?: string;
}> = ({ host, routesScope }) => {
  useRegisterExperiments();
  const loginState = useLoginState();
  const [activeItem, setActiveItem] = React.useState<ActiveItem | undefined>();
  const [disableCookieConsentBanner, setDisableCookieConsentBanner] =
    React.useState(false);
  const { isPreview = false, visitorId } = useRuntimeConfig();
  const favicon = isPreview ? 'data:,' : '/favicon.ico';

  return (
    <XingHubProvider value={{ cookies, visitorId }}>
      <Helmet>
        <link rel="icon" href={favicon} />
      </Helmet>
      <GlobalStyles>
        {loginState.isLoggedIn ? (
          <LoggedInFrameProvider
            activeItem={activeItem}
            internalPaths={internalPaths}
            userConsentSettings={{ disableBanner: disableCookieConsentBanner }}>
            <JobsX3Redirects />
            {/* Skeleton adds conditional rendering for children and displays a
              loading state if the data is not there yet */}
            <host.XingletLoader name="@xingternal/crate-communication-core-tracking" />
            <Routing
              setActiveItem={setActiveItem}
              setDisableCookieConsentBanner={setDisableCookieConsentBanner}
              routesScope={routesScope}
            />
            <host.XingletLoader
              name="@xingternal/crate-marketing-braze"
              ttt={ttt}
            />
            <NoSSR>
              <ScrollManager />
            </NoSSR>
          </LoggedInFrameProvider>
        ) : (
          <LoggedOutFrameProvider
            internalPaths={internalPaths}
            userConsentSettings={{ disableBanner: disableCookieConsentBanner }}>
            <host.XingletLoader name="@xingternal/crate-communication-core-tracking" />
            <Routing
              setActiveItem={setActiveItem}
              setDisableCookieConsentBanner={setDisableCookieConsentBanner}
              routesScope={routesScope}
            />
            <NoSSR>
              <ScrollManager />
            </NoSSR>
          </LoggedOutFrameProvider>
        )}
      </GlobalStyles>
    </XingHubProvider>
  );
};
export default class RootXinglet implements Xinglet {
  public getComponent(
    host: RuntimeHost
  ): React.ComponentType<{ basePath: string; routesScope?: string }> {
    return ({ basePath, routesScope }) => {
      return (
        <host.XingletLoader
          name="@xingternal/crate-core-hops-environment"
          basePath={basePath}>
          {/* Disable scaling of viewport as early as possible */}
          <Helmet>
            <title>XING</title>
            <meta
              name="viewport"
              content="width=device-width, minimum-scale=1, maximum-scale=1"
            />
          </Helmet>
          <RootProvider>
            <Root host={host} routesScope={routesScope} />
          </RootProvider>
        </host.XingletLoader>
      );
    };
  }
}
